import React, { useState, useEffect, useRef, forwardRef } from 'react';

import { SelectItem as SelectItemType, Select, SelectProps, Text, Group } from '@mantine/core';
import useAutoCompleteGoogleCalendars from 'hooks/googleCalendar/useAutoCompleteGoogleCalendars';
import { GoogleCalendarAutoComplete as GoogleCalendarAutoCompleteType } from 'types';

export interface GoogleCalendarSelectValue extends SelectItemType {
  googleCalendarId: string;
  isPrimary: boolean;
  label: string;
}

interface IGoogleCalendarSelectProps
  extends Omit<SelectProps, 'data' | 'onChange' | 'filter' | 'onItemSubmit' | 'rightSection' | 'clearable'> {
  googleCalendarIntegrationId: string;
}

const SelectItem = forwardRef<HTMLDivElement, GoogleCalendarSelectValue>(
  ({ image, label, isPrimary, ...others }: GoogleCalendarSelectValue, ref) => (
    <Group ref={ref} {...others} spacing='xs'>
      <Text size='md'>{label}</Text>
      {isPrimary && (
        <Text size='md' opacity={0.65}>
          (Primary)
        </Text>
      )}
    </Group>
  ),
);

const GoogleCalendarSelect = ({
  googleCalendarIntegrationId,
  ...autocompleteProps
}: IGoogleCalendarSelectProps): JSX.Element => {
  const inputRef = useRef<HTMLInputElement>(null);

  const [options, setOptions] = useState<GoogleCalendarSelectValue[]>([]);

  const { data, isSuccess } = useAutoCompleteGoogleCalendars({
    googleCalendarIntegrationId: googleCalendarIntegrationId,
    isWriteable: true,
  });

  useEffect(() => {
    if (isSuccess && data) {
      setOptions(
        data.data.map((googleCalendar: GoogleCalendarAutoCompleteType) => ({
          ...googleCalendar,
          value: googleCalendar.googleCalendarId,
          label: googleCalendar.calendarName,
        })),
      );
    }
  }, [data, isSuccess]);

  return (
    <Select
      ref={inputRef}
      itemComponent={SelectItem}
      data={options}
      clearable={false}
      {...autocompleteProps}
    />
  );
};

export default GoogleCalendarSelect;
